// cutscene-message.ts — the PURE read-model + parser for the in-mission CUTSCENE
// player DOM port (HTML/DOM UI shell plan, Phase 5 — kidfCutScene 1047). Reproduces
// the canvas CutSceneForm.More() tokenization (game/CutScene.cpp:97-184 ↔
// client/src/ui/cutscene.ts:104-177) WITHOUT a canvas/DOM/font:
//   - the cutscene text is narration interleaved with inline tags:
//       <img NAME>  — switch the displayed image to NAME (a .rbm authored as a .png
//                     atlas entry); the text box then REFLOWS below the new image;
//       <snd N>     — play sfx ksfxHappyEnding + N;
//   - between tags is narration that is TYPED OUT by the typewriter (the SAME
//     EcomTextControl reveal the E-Com uses — speaker @A/@J/@O/@F markers are honored
//     identically, so the narration reuses parseEcomMessage/revealSegments);
//   - More() emits text up to the next <img> tag as a PAGE; an <img> swaps the image
//     + relayouts; a <snd> plays; when the text is exhausted the button dismisses.
//
// The C++ More() consumes ALL leading tags at the front of the remaining text (an
// <img> swaps the image, a <snd> plays), then a single run of narration up to the
// next '<' (or end). We tokenize the WHOLE message ONCE into an ordered list of
// "image segments": each segment is one image (or null before the first <img>) plus
// the narration that runs under it, with any <snd> stingers flagged at the points
// they fire. The VIEW then box-fits each segment's narration into typewriter pages
// (the DOM measurement the canvas does with Font::CalcBreak) and plays the sounds.
//
// Pure (no DOM) so the <img>/<snd> tag parsing is unit-tested (dom-cutscene.test.ts).

import { IniScanf } from '../../../../sim/src/data/ini.ts';

// game/soundeffects.h — ksfxHappyEnding (the <snd n> base index; cutscene.ts:32).
export const ksfxHappyEnding = 181;

/** One image segment of a parsed cutscene: the image to display while this segment's
 *  narration types out (null before the first <img>, i.e. the leading narration), the
 *  narration TEXT that runs under it (may carry @A/@J/@O/@F speaker markers — fed to
 *  the SAME ecom parser/reveal), and the sfx indices that fire when this segment opens
 *  (the <snd N> tags consumed with the leading <img> for this segment, already mapped
 *  to ksfxHappyEnding + N). */
export interface CutSceneSegment {
  /** the atlas bitmap name to show (e.g. 'orbitshot4.rbm'); null = no image yet. */
  image: string | null;
  /** the narration that types out while this image shows (no tags). */
  text: string;
  /** the absolute sfx indices to play when this segment is reached (ksfxHappyEnding + N). */
  sfx: number[];
}

/** The whole parsed cutscene: the ordered image segments (More() pages, before the
 *  per-segment box-fit the view applies). */
export interface ParsedCutScene {
  segments: CutSceneSegment[];
}

/** Parse a cutscene message into ordered image segments, faithfully reproducing the
 *  CutSceneForm.More() tokenizer (cutscene.ts:104-150): walk the text; at a '<' that
 *  begins a run (no narration accumulated yet), consume an `<img NAME>` (switch image)
 *  or `<snd N>` (queue the stinger) tag; otherwise accumulate narration up to the next
 *  '<' (or end). A new <img> begins a new segment; <snd> tags attach to the segment
 *  they sit in. Leading narration before any <img> becomes a segment with image=null
 *  (the canvas would type it under the empty/last bitmap). */
export function parseCutScene(message: string): ParsedCutScene {
  const segments: CutSceneSegment[] = [];
  // the segment under construction; flushed to `segments` when an <img> opens a new
  // one or at end. A segment with neither image, text, nor sfx is never emitted.
  let cur: CutSceneSegment = { image: null, text: '', sfx: [] };
  let started = false; // whether `cur` has had any image/text/sfx assigned

  const flush = (): void => {
    if (started) {
      segments.push(cur);
    }
    cur = { image: null, text: '', sfx: [] };
    started = false;
  };

  let i = 0;
  while (i < message.length) {
    if (message[i] === '<') {
      const rest = message.slice(i);
      const rImg = IniScanf(rest, '<img %s>%+');
      if (rImg.count !== 0) {
        // an <img> tag: it OPENS a new image segment (cutscene.ts:128-131 ShowBitmap
        // + Layout + slice). Flush any segment in progress, then start a fresh one.
        // rImg.count===1 means the tag ran to end-of-string (no '>' offset captured).
        const advance = rImg.count === 1 ? rest.length : (rImg.values[1] as number);
        flush();
        cur.image = rImg.values[0] as string;
        started = true;
        i += advance;
        continue;
      }
      const rSnd = IniScanf(rest, '<snd %d>%+');
      if (rSnd.count !== 0) {
        // a <snd> tag (cutscene.ts:134-145 PlaySfx): attach to the current segment, fired
        // when the segment opens.
        // KNOWN DEVIATION (no shipped content hits it): the canvas More() breaks the page
        // at ANY non-leading '<', so a <snd> placed mid-narration ("text<snd N>more") would
        // page-break before it on the canvas (the stinger plays at the START of the next
        // page). We fold it into the current segment instead. EVERY shipped .ld places <snd>
        // immediately after <img> (position 0), where both agree — so this is invisible in
        // practice; documented rather than page-split for a case that never occurs.
        const advance = rSnd.count === 1 ? rest.length : (rSnd.values[1] as number);
        cur.sfx.push(ksfxHappyEnding + (rSnd.values[0] as number));
        started = true;
        i += advance;
        continue;
      }
      // a '<' that is neither <img> nor <snd> — treat it as plain narration text.
      cur.text += '<';
      started = true;
      i++;
    } else {
      // accumulate narration up to the next '<' (or end).
      const next = message.indexOf('<', i);
      const end = next === -1 ? message.length : next;
      cur.text += message.slice(i, end);
      started = true;
      i = end;
    }
  }
  flush();

  return { segments };
}
